import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  InputAdornment,
  Button,
  Grid,
  Autocomplete,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Typography,
  Box,
  Tooltip,
  Modal,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { useAuth } from "../../Auth/AuthContext";
import { fetchCustomers, fetchEmployees, postWorkOrder, fetchPoolByCustomer, fetchAllWorkOrders, updateWorkOrder } from "../../Utils/api";
import PopupGeneric from "../PopupGeneric/pop-up-generic";
import HomeIcon from "@mui/icons-material/Home";
import SearchIcon from "@mui/icons-material/Search";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import { Engineering } from "@mui/icons-material";
import CalendarToday from "@mui/icons-material/CalendarToday";

const CreateWorkOrderModal = ({ open, onClose, onSuccess }) => {
  const [customerList, setCustomerList] = useState([]);
  const [technicianList, setTechnicianList] = useState([]);
  const [searchCustomer, setSearchCustomer] = useState(null);
  const [selectedTechnician, setSelectedTechnician] = useState("");
  const [occurrence, setOccurrence] = useState("");
  const [address, setAddress] = useState("");
  const [selectedTask, setSelectedTask] = useState("");
  const [additionalComments, setAdditionalComments] = useState("");
  const [date, setDate] = useState(null);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");
  const [popupTitle, setPopupTitle] = useState("");
  const [highlightErrors, setHighlightErrors] = useState({});
  const [poolId, setPoolId] = useState(null);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingTechnicians, setIsLoadingTechnicians] = useState(false);
  // Cross-employee conflict resolution (mirrors the route builder's "Customer Already Scheduled")
  const [conflictPopupVisible, setConflictPopupVisible] = useState(false);
  const [conflictInfo, setConflictInfo] = useState(null); // { otherEmployeeName, conflictWorkOrders, dayName }
  const [pendingWorkOrderData, setPendingWorkOrderData] = useState(null);
  const { companyId } = useAuth();

  const MAX_COMMENT_LENGTH = 1000;
  const today = dayjs();
  const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // Day-of-week (0=Sun..6=Sat) of a work order, from its due/initial date (noon-safe).
  const getWorkOrderDayIndex = (wo) => {
    const dateStr = (wo.workOrder?.workOrderDueDate?.trim() || wo.workOrder?.initialServiceDate?.trim() || "");
    if (!dateStr) return -1;
    try {
      return new Date(dateStr.split("T")[0] + "T12:00:00").getDay();
    } catch (e) {
      return -1;
    }
  };

  // Load Customers
  useEffect(() => {
    if (open) {
      const loadCustomers = async () => {
        setIsLoadingCustomers(true);
        try {
          const data = await fetchCustomers(companyId, "Y", null);
          const formattedData = data.map((customer) => ({
            id: customer.customerId,
            name: `${customer.custFirstName} ${customer.custLastName}`,
            address: `${customer.custAddress}, ${customer.custCity}, ${customer.custZip}`,
          }));
          setCustomerList(formattedData);
        } catch (error) {
          console.error("Error loading customers:", error);
        } finally {
          setIsLoadingCustomers(false);
        }
      };
      loadCustomers();
    }
  }, [companyId, open]);

  // Load Technicians
  useEffect(() => {
    if (open) {
      const loadTechnicians = async () => {
        setIsLoadingTechnicians(true);
        try {
          const data = await fetchEmployees(companyId, null, "Technician");
          const formattedData = data.map((tech) => ({
            id: tech.employeeId,
            name: `${tech.firstName} ${tech.lastName}`,
            address: `${tech.empAddress}, ${tech.empCity}, ${tech.empZipCode}`,
          }));
          setTechnicianList(formattedData);
        } catch (error) {
          console.error("Error loading technicians:", error);
        } finally {
          setIsLoadingTechnicians(false);
        }
      };
      loadTechnicians();
    }
  }, [companyId, open]);

  const fetchPoolIdByCustomer = useCallback(async (customerId) => {
    try {
      const response = await fetchPoolByCustomer(companyId, customerId);
      if (response && response.poolId) {
        setPoolId(response.poolId);
      } else {
        setPoolId(null);
      }
    } catch (error) {
      console.error("Error fetching pool ID:", error);
      setPoolId(null);
    }
  }, [companyId]);

  useEffect(() => {
    if (selectedTask === "On Demand") {
      setOccurrence("One time");
    } else if (selectedTask === "Regular Service") {
      setOccurrence("Weekly");
    } else if (selectedTask === "Back-wash") {
      setOccurrence("Quarterly");
    } else if (selectedTask === "Filter breakdown") {
      setOccurrence("Twice a year");
    } else {
      setOccurrence("");
    }
  }, [selectedTask]);

  useEffect(() => {
    if (searchCustomer) {
      setAddress(searchCustomer.address);
      fetchPoolIdByCustomer(searchCustomer.id);
    } else {
      setAddress("");
      setPoolId(null);
    }
  }, [searchCustomer, fetchPoolIdByCustomer]);

  useEffect(() => {
    const isFormValid =
      selectedTask &&
      searchCustomer &&
      selectedTechnician &&
      date &&
      (selectedTask === "On Demand" ? additionalComments : occurrence);

    setIsSubmitDisabled(!isFormValid);
  }, [selectedTask, searchCustomer, selectedTechnician, occurrence, additionalComments, date]);

  const resetForm = () => {
    setSearchCustomer(null);
    setSelectedTechnician("");
    setOccurrence("");
    setAddress("");
    setSelectedTask("");
    setAdditionalComments("");
    setDate(null);
    setHighlightErrors({});
  };

  // Actually create the work order and show the success/error popup. Shared by the normal
  // path and by both conflict resolutions.
  const createWorkOrderAndFinish = async (data) => {
    try {
      await postWorkOrder(data);
      setPopupMessage("Work order created successfully!");
      setPopupTitle("Success");
      setPopupVisible(true);
      resetForm();
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Error submitting work order:", error);
      setPopupMessage("Error creating work order. Please try again.");
      setPopupTitle("Error");
      setPopupVisible(true);
    }
  };

  // "Permanent" — move the customer here: cancel the other employee's weekly route(s) for
  // this customer on this day, then create the new permanent weekly route.
  const handleConflictPermanent = async () => {
    setConflictPopupVisible(false);
    for (const wo of conflictInfo?.conflictWorkOrders || []) {
      try {
        await updateWorkOrder({ ...wo.workOrder, statusCode: "Cancelled" });
      } catch (err) {
        console.warn("Could not cancel conflicting work order:", err);
      }
    }
    if (pendingWorkOrderData) {
      await createWorkOrderAndFinish(pendingWorkOrderData);
    }
    setConflictInfo(null);
    setPendingWorkOrderData(null);
  };

  // "This week only" — one-time cover for this technician (occurrence 0, temporary); the
  // other employee keeps their permanent weekly route untouched.
  const handleConflictThisWeek = async () => {
    setConflictPopupVisible(false);
    if (pendingWorkOrderData) {
      await createWorkOrderAndFinish({
        ...pendingWorkOrderData,
        workOrderOccurance: 0,
        isTemporary: true,
      });
    }
    setConflictInfo(null);
    setPendingWorkOrderData(null);
  };

  const handleConflictCancel = () => {
    setConflictPopupVisible(false);
    setConflictInfo(null);
    setPendingWorkOrderData(null);
  };

  const handleSubmit = async () => {
    const errors = {};

    if (!selectedTask) errors.selectedTask = true;
    if (!searchCustomer) errors.searchCustomer = true;
    if (!selectedTechnician) errors.selectedTechnician = true;
    if (selectedTask === "On Demand" && !additionalComments) errors.additionalComments = true;
    if (selectedTask !== "On Demand" && !occurrence) errors.occurrence = true;
    if (!date) errors.date = true;

    if (Object.keys(errors).length > 0) {
      setPopupMessage("Please fill in all required fields.");
      setPopupTitle("Alert");
      setHighlightErrors(errors);
      setPopupVisible(true);
      return;
    }

    const occurrenceMap = {
      "One time": 0,
      "Daily": 1,
      "Weekly": 7,
      "Every other week": 14,
      "Once a month": 30,
      "Quarterly": 90,
      "Twice a year": 180,
      "Yearly": 365
    };
    const finalOccurence = occurrenceMap[occurrence] ?? 0;
    const finalType = selectedTask === "On Demand" ? "On Demand" : "Service";
    const workOrderData = {
      poolId: poolId || null,
      customerId: searchCustomer?.id || null,
      employeeId: selectedTechnician?.id || null,
      workOrderType: finalType,
      workOrderOccurance: finalOccurence,
      workOrderPart: null,
      workOrderCost: null,
      workOrderComment: additionalComments || null,
      workOrderDueDate: date ? dayjs(date).hour(12).minute(0).second(0).millisecond(0).toISOString() : null,
      lastServiceDate: null,
      initialServiceDate: date ? dayjs(date).hour(12).minute(0).second(0).millisecond(0).toISOString() : null,
      statusCode: "Not Started",
      workOrderServiceType: selectedTask,
    };

    // Cross-employee duplicate guard. A permanent weekly route for a customer who is already
    // on a DIFFERENT employee's route the same day would double-book them. (Same-employee
    // same-slot duplicates are handled server-side in createWorkOrder.) Offer the same choice
    // the route builder does: move them permanently, cover this week only, or cancel.
    const isPermanentWeekly = finalType === "Service" && finalOccurence === 7;
    if (isPermanentWeekly) {
      try {
        const targetDay = dayjs(date).day();
        const allWOs = await fetchAllWorkOrders(companyId).catch(() => []);
        const conflicts = (Array.isArray(allWOs) ? allWOs : []).filter((wo) => {
          const w = wo.workOrder;
          if (!w) return false;
          if (w.customerId !== searchCustomer?.id) return false;
          if (w.employeeId === selectedTechnician?.id) return false; // same employee → backend guard handles it
          if ((w.workOrderOccurance ?? 0) !== 7) return false;
          if ((w.workOrderType ?? "Service") !== "Service") return false;
          if (w.statusCode?.toLowerCase() === "cancelled") return false;
          if (w.isTemporary === true || w.isTemporary === "Y") return false;
          return getWorkOrderDayIndex(wo) === targetDay;
        });
        if (conflicts.length > 0) {
          const otherEmp = technicianList.find((t) => t.id === conflicts[0].workOrder.employeeId);
          setConflictInfo({
            otherEmployeeName: otherEmp?.name || "another technician",
            conflictWorkOrders: conflicts,
            dayName: DAY_NAMES[targetDay] || "that day",
          });
          setPendingWorkOrderData(workOrderData);
          setConflictPopupVisible(true);
          return;
        }
      } catch (e) {
        console.warn("Cross-employee conflict check failed; proceeding to create:", e);
      }
    }

    await createWorkOrderAndFinish(workOrderData);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            padding: "20px",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              padding: "20px",
              width: "60%",
              textAlign: "center",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: "bold", marginBottom: "20px" }}>
              Create Route
            </Typography>

            <Grid container spacing={2}>
              {/* Search Customer */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={customerList}
                  getOptionLabel={(option) => option.name}
                  value={searchCustomer}
                  onChange={(e, newValue) => setSearchCustomer(newValue)}
                  loading={isLoadingCustomers}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Customer"
                      variant="outlined"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      error={!!highlightErrors.searchCustomer}
                      sx={{ "& .MuiInputBase-input": { textAlign: "left" } }}
                    />
                  )}
                  sx={{ "& .MuiAutocomplete-input": { textAlign: "left" } }}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Address"
                  value={address}
                  required
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <HomeIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiInputBase-input": { textAlign: "left" } }}
                  variant="outlined"
                  disabled
                />
              </Grid>

              {/* Technician */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={technicianList}
                  getOptionLabel={(option) => option?.name || ""}
                  value={selectedTechnician}
                  onChange={(e, newValue) => setSelectedTechnician(newValue)}
                  loading={isLoadingTechnicians}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign Technician"
                      variant="outlined"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Engineering />
                          </InputAdornment>
                        ),
                      }}
                      error={!!highlightErrors.selectedTechnician}
                    />
                  )}
                />
              </Grid>

              {/* Task/Service */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={["Regular Service", "Back-wash", "Filter breakdown", "On Demand"]}
                  value={selectedTask}
                  onChange={(e, newValue) => setSelectedTask(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Task/Service"
                      variant="outlined"
                      required
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <TaskAltIcon />
                          </InputAdornment>
                        ),
                      }}
                      error={!!highlightErrors.selectedTask}
                      sx={{ "& .MuiInputBase-input": { textAlign: "left" } }}
                    />
                  )}
                  sx={{ "& .MuiAutocomplete-input": { textAlign: "left" } }}
                />
              </Grid>

              {/* Additional Comments */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Further Comments"
                  required={selectedTask === "On Demand"}
                  value={additionalComments}
                  onChange={(e) => {
                    if (e.target.value.length <= MAX_COMMENT_LENGTH) {
                      setAdditionalComments(e.target.value);
                    }
                  }}
                  multiline
                  rows={3}
                  variant="outlined"
                  error={!!highlightErrors.additionalComments}
                  helperText={`${MAX_COMMENT_LENGTH - additionalComments.length} characters remaining`}
                />
              </Grid>

              {/* Occurrence */}
              <Grid item xs={12} sm={6}>
                <Tooltip
                  title={selectedTask === "On Demand" ? "On Demand tasks are automatically set to 'One time'" : ""}
                  arrow
                >
                  <FormControl
                    fullWidth
                    variant="outlined"
                    required
                    error={!!highlightErrors.occurrence}
                  >
                    <InputLabel id="occurrence-label">Occurrence</InputLabel>
                    <Select
                      labelId="occurrence-label"
                      label="Occurrence"
                      value={occurrence}
                      onChange={(e) => setOccurrence(e.target.value)}
                      disabled={selectedTask === "On Demand"}
                      sx={{ "& .MuiInputBase-input": { textAlign: "left" } }}
                    >
                    <MenuItem value="">
                      <em>Clear Selection</em>
                    </MenuItem>
                    <MenuItem value="One time" disabled={selectedTask !== "On Demand"}
                    style={{ display: selectedTask === "On Demand" ? "block" : "none" }}>
                      One time
                    </MenuItem>
                    <MenuItem value="Daily" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Daily</MenuItem>
                    <MenuItem value="Weekly" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Weekly</MenuItem>
                    <MenuItem value="Every other week" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Every other week</MenuItem>
                    <MenuItem value="Once a month" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Once a month</MenuItem>
                    <MenuItem value="Quarterly" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Quarterly</MenuItem>
                    <MenuItem value="Twice a year" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Twice a year</MenuItem>
                    <MenuItem value="Yearly" style={{ display: selectedTask !== "On Demand" ? "block" : "none" }}>Yearly</MenuItem>
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>

              {/* Date Field */}
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Service Start Date"
                  value={date}
                  onChange={(newValue) => setDate(newValue)}
                  minDate={today}
                  required
                  slotProps={{
                    textField: {
                      error: !!highlightErrors.date,
                      InputProps: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <CalendarToday />
                          </InputAdornment>
                        ),
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>

            {/* Submit and Cancel Buttons */}
            <Button
              variant="contained"
              sx={{
                marginTop: "20px",
                width: "100%",
                padding: "12px",
                fontWeight: "bold",
              }}
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              sx={{
                marginTop: "10px",
                width: "100%",
                fontWeight: "bold",
              }}
              onClick={handleClose}
            >
              Cancel
            </Button>
          </Paper>

          {/* Popup */}
          <PopupGeneric
            isVisible={isPopupVisible}
            onClose={() => {
              setPopupVisible(false);
              if (popupTitle === "Success") {
                handleClose();
              }
            }}
            title={popupTitle}
            content={<p>{popupMessage}</p>}
            actions={
              <button
                onClick={() => {
                  setPopupVisible(false);
                  if (popupTitle === "Success") {
                    handleClose();
                  }
                }}
                style={{
                  marginTop: '10px',
                  padding: '5px 10px',
                  borderRadius: '5px',
                  backgroundColor: '#003366',
                  color: '#fff',
                  border: 'none',
                  cursor: 'pointer',
                }}
              >
                OK
              </button>
            }
          />

          {/* Cross-employee conflict popup */}
          <PopupGeneric
            isVisible={conflictPopupVisible}
            onClose={handleConflictCancel}
            title="Customer Already Scheduled"
            content={
              <p>
                <strong>{searchCustomer?.name}</strong> is already on{" "}
                <strong>{conflictInfo?.otherEmployeeName}</strong>'s {conflictInfo?.dayName} route.
                {" "}How do you want to handle it?
              </p>
            }
            actions={
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", justifyContent: "center", marginTop: "10px" }}>
                <button
                  onClick={handleConflictThisWeek}
                  style={{ padding: "6px 12px", borderRadius: "5px", backgroundColor: "#f57c00", color: "#fff", border: "none", cursor: "pointer" }}
                >
                  This week only
                </button>
                <button
                  onClick={handleConflictPermanent}
                  style={{ padding: "6px 12px", borderRadius: "5px", backgroundColor: "#003366", color: "#fff", border: "none", cursor: "pointer" }}
                >
                  Permanent
                </button>
                <button
                  onClick={handleConflictCancel}
                  style={{ padding: "6px 12px", borderRadius: "5px", backgroundColor: "#9e9e9e", color: "#fff", border: "none", cursor: "pointer" }}
                >
                  Cancel
                </button>
              </div>
            }
          />
        </Box>
      </LocalizationProvider>
    </Modal>
  );
};

export default CreateWorkOrderModal;
